import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useLivePaymentLinkQuery } from './useLivePaymentLinkQuery';
import { useAgreementLeftToPayQuery } from './useAgreementLeftToPayQuery';
import { agreementKeys } from './agreementKeys';
import { useMotorplanPaymentForm } from './useMotorplanPaymentForm';
import type { MotorplanPaymentFormState } from './useMotorplanPaymentForm';
import { minimumTopUpMinor } from '../utils/topUpAmount';
import { motorplanAvailability, partPaymentMaxMinor } from '../utils/motorplanAvailability';
import type { MotorplanAvailability } from '../utils/motorplanAvailability';
import type { PaymentLinkSummary } from '../types/paymentLinks.types';
import type { Agreement } from '../types/agreements.types';

export interface MotorplanPaymentState {
    availability: MotorplanAvailability;
    leftToPay: string;
    liveLink: PaymentLinkSummary | null;
    formOpen: boolean;
    openForm: () => void;
    optionsHidden: boolean;
    optionsRevealed: boolean;
    revealOptions: () => void;
    form: MotorplanPaymentFormState;
}

/** Owns the left-to-pay read, what it allows, and the link form, which closes while a link is live;
 *  a link already live on arrival also hides the payment options until asked for. */
export function useMotorplanPayment(
    agreementId: string | undefined,
    agreement: Agreement | undefined,
    shortfallScaled: number,
    onAccessRefused?: () => void,
): MotorplanPaymentState {
    const queryClient = useQueryClient();
    const leftToPay = useAgreementLeftToPayQuery(agreementId, agreement?.elemisAgreementRef);
    const leftToPayData = leftToPay.isError ? undefined : leftToPay.data;
    const availability = motorplanAvailability(leftToPayData, leftToPay.isLoading, shortfallScaled);
    const minimumMinor = minimumTopUpMinor(shortfallScaled);
    const maximumMinor = leftToPayData?.eligible ? partPaymentMaxMinor(leftToPayData.leftToPay) : minimumMinor;

    const liveLinkQuery = useLivePaymentLinkQuery(agreementId, shortfallScaled > 0);
    const [changingLink, setChangingLink] = useState(false);
    const [optionsRevealed, setOptionsRevealed] = useState(false);

    const onSent = useCallback(() => {
        setChangingLink(false);
        if (!agreementId) return;
        void queryClient.invalidateQueries({ queryKey: agreementKeys.history(agreementId) });
        const liveLinkKey = agreementKeys.livePaymentLink(agreementId);
        // A pending first read has no data, so invalidating alone would reuse it and its stale answer.
        void queryClient
            .cancelQueries({ queryKey: liveLinkKey })
            .then(() => queryClient.invalidateQueries({ queryKey: liveLinkKey }));
    }, [queryClient, agreementId]);
    const leftToPayFigure = leftToPayData?.eligible ? leftToPayData.leftToPay : '';
    const form = useMotorplanPaymentForm(agreementId ?? '', minimumMinor, maximumMinor, onSent, onAccessRefused, {
        partPaymentOffered: availability.available && availability.partPayment,
        leftToPay: leftToPayFigure,
    });

    const liveLink = liveLinkQuery.data ?? null;
    const awaitingSentLinkRead = form.sentLink !== null && liveLinkQuery.isFetching;
    const formOpen = changingLink || (liveLink === null && !awaitingSentLinkRead);

    return {
        availability,
        leftToPay: leftToPayFigure,
        liveLink,
        formOpen,
        openForm: () => setChangingLink(true),
        optionsHidden: liveLink !== null && form.sentLink === null && !optionsRevealed,
        optionsRevealed,
        revealOptions: () => setOptionsRevealed(true),
        form,
    };
}
